/**
 * Optimized image processing implementations.
 *
 * These are alternative implementations that may offer better performance but
 * need to be validated against production results before use.
 *
 * Each operation is controlled by a separate feature flag to help identify
 * which specific optimization causes differences compared to production.
 *
 * Environment variables:
 * - CM_OPT_CONVOLVE_SHARP=true - Use Sharp for convolution
 * - CM_OPT_EXTRACT_CHANNEL=true - Use optimized channel extraction
 * - CM_OPT_COMBINE_CHANNELS=true - Use optimized channel combining
 * - CM_OPT_LOGGING=true - Enable operation logging
 */

import type {MultichannelLayouts, TypedImageData} from "@cm/utils/typed-image-data"
import type {LUTData} from "#image-processing/operations/tone-mapping"
import {castToFloat32Array} from "@cm/utils"
import {createImageData} from "#image-processing/utils/typed-image-data-utils"
import {OptimizedOps} from "#image-processing/utils/environment"
import {createOpLogger} from "#image-processing/utils/operation-logger"

export type ImageData = TypedImageData

// Helper function to get image buffer as Float32Array
function getImageBufferFloat32(image: ImageData): Float32Array {
    if (image.dataType !== "float32") {
        throw new Error(`Invalid image data type: ${image.dataType}`)
    }
    return castToFloat32Array(image.data)
}

function getImageBufferRGBAFloat32(image: ImageData): Float32Array {
    if (image.dataType !== "float32") {
        throw new Error(`Invalid image data type: ${image.dataType}`)
    } else if (image.channelLayout !== "RGBA") {
        throw new Error(`Invalid image channel layout: ${image.channelLayout}`)
    }
    return castToFloat32Array(image.data)
}

function ensureOutputLike(image: Omit<ImageData, "data">, output: ImageData | undefined): ImageData {
    if (!output) {
        output = createImageData(image.width, image.height, image.channelLayout, image.dataType, image.colorSpace, image.dpi)
    }
    return output
}

/**
 * Optimized 3D LUT application with tetrahedral interpolation.
 *
 * Same math as the legacy applyLUT (tetrahedral mode), but avoids
 * per-pixel array allocations and function-call overhead by inlining
 * the LUT lookups and boundary checks.
 *
 * Enable with: CM_OPT_APPLY_LUT=true
 */
export function applyLutOptimized(image: ImageData, LUT: LUTData, output?: ImageData): ImageData {
    const opLog = OptimizedOps.logging() ? createOpLogger() : null
    output = ensureOutputLike(image, output)
    const buffer = getImageBufferRGBAFloat32(image)
    const outBuffer = getImageBufferRGBAFloat32(output)

    if (LUT.channels !== 3) {
        throw new Error("applyLutOptimized: LUT channels must be 3")
    }

    const sz = Math.round(LUT.size)
    const sz_1 = Math.round(LUT.size - 1)
    const lutData = LUT.data
    const inScale = Math.fround(sz_1 / LUT.range)

    // Pre-compute strides for 3D LUT indexing
    // LUT layout: lutData[z * zStride + y * yStride + x * xStride + channel]
    const xStride = 3
    const yStride = sz * 3
    const zStride = sz * sz * 3

    // Boundary-aware LUT read: returns 0 if the unclamped coordinate < 0,
    // 1 if > sz_1, otherwise the actual LUT value. This matches the
    // legacy minMaxMapped behaviour.
    const minMaxMapped = (v: number, i: number): number => (v < 0 ? 0 : v > sz_1 ? 1 : lutData[i])

    const len = buffer.length

    for (let ofs = 0; ofs < len; ofs += 4) {
        // Scale input to LUT coordinates
        const x = buffer[ofs] * inScale // x = input red
        const y = buffer[ofs + 1] * inScale // y = input green
        const z = buffer[ofs + 2] * inScale // z = input blue

        // Integer indices (unclamped – may be negative or exceed sz_1)
        const x0i = Math.floor(x)
        const y0i = Math.floor(y)
        const z0i = Math.floor(z)
        const x1i = Math.ceil(x)
        const y1i = Math.ceil(y)
        const z1i = Math.ceil(z)

        // Clamped indices for safe LUT array access
        const x0c = Math.max(0, Math.min(sz_1, x0i))
        const y0c = Math.max(0, Math.min(sz_1, y0i))
        const z0c = Math.max(0, Math.min(sz_1, z0i))
        const x1c = Math.max(0, Math.min(sz_1, x1i))
        const y1c = Math.max(0, Math.min(sz_1, y1i))
        const z1c = Math.max(0, Math.min(sz_1, z1i))

        // Fractional parts for interpolation
        const xf = x - x0i
        const yf = y - y0i
        const zf = z - z0i

        // Pre-compute base indices for all 8 cube corners (using clamped indices)
        const i000 = x0c * xStride + y0c * yStride + z0c * zStride
        const i001 = x0c * xStride + y0c * yStride + z1c * zStride
        const i010 = x0c * xStride + y1c * yStride + z0c * zStride
        const i011 = x0c * xStride + y1c * yStride + z1c * zStride
        const i100 = x1c * xStride + y0c * yStride + z0c * zStride
        const i101 = x1c * xStride + y0c * yStride + z1c * zStride
        const i110 = x1c * xStride + y1c * yStride + z0c * zStride
        const i111 = x1c * xStride + y1c * yStride + z1c * zStride

        // Read all 8 corner values with boundary checks (24 lookups)
        // Each channel checks its own axis: red→x, green→y, blue→z
        const r000 = minMaxMapped(x0i, i000),
            g000 = minMaxMapped(y0i, i000 + 1),
            b000 = minMaxMapped(z0i, i000 + 2)
        const r001 = minMaxMapped(x0i, i001),
            g001 = minMaxMapped(y0i, i001 + 1),
            b001 = minMaxMapped(z1i, i001 + 2)
        const r010 = minMaxMapped(x0i, i010),
            g010 = minMaxMapped(y1i, i010 + 1),
            b010 = minMaxMapped(z0i, i010 + 2)
        const r011 = minMaxMapped(x0i, i011),
            g011 = minMaxMapped(y1i, i011 + 1),
            b011 = minMaxMapped(z1i, i011 + 2)
        const r100 = minMaxMapped(x1i, i100),
            g100 = minMaxMapped(y0i, i100 + 1),
            b100 = minMaxMapped(z0i, i100 + 2)
        const r101 = minMaxMapped(x1i, i101),
            g101 = minMaxMapped(y0i, i101 + 1),
            b101 = minMaxMapped(z1i, i101 + 2)
        const r110 = minMaxMapped(x1i, i110),
            g110 = minMaxMapped(y1i, i110 + 1),
            b110 = minMaxMapped(z0i, i110 + 2)
        const r111 = minMaxMapped(x1i, i111),
            g111 = minMaxMapped(y1i, i111 + 1),
            b111 = minMaxMapped(z1i, i111 + 2)

        // Tetrahedral interpolation - select tetrahedron based on fractional ordering
        let r: number, g: number, b: number

        if (xf > yf) {
            if (yf > zf) {
                // r > g > b
                r = r000 + xf * (r100 - r000) + yf * (r110 - r100) + zf * (r111 - r110)
                g = g000 + xf * (g100 - g000) + yf * (g110 - g100) + zf * (g111 - g110)
                b = b000 + xf * (b100 - b000) + yf * (b110 - b100) + zf * (b111 - b110)
            } else if (xf > zf) {
                // r > b > g
                r = r000 + xf * (r100 - r000) + yf * (r111 - r101) + zf * (r101 - r100)
                g = g000 + xf * (g100 - g000) + yf * (g111 - g101) + zf * (g101 - g100)
                b = b000 + xf * (b100 - b000) + yf * (b111 - b101) + zf * (b101 - b100)
            } else {
                // b > r > g
                r = r000 + xf * (r101 - r001) + yf * (r111 - r101) + zf * (r001 - r000)
                g = g000 + xf * (g101 - g001) + yf * (g111 - g101) + zf * (g001 - g000)
                b = b000 + xf * (b101 - b001) + yf * (b111 - b101) + zf * (b001 - b000)
            }
        } else {
            if (zf > yf) {
                // b > g > r
                r = r000 + xf * (r111 - r011) + yf * (r011 - r001) + zf * (r001 - r000)
                g = g000 + xf * (g111 - g011) + yf * (g011 - g001) + zf * (g001 - g000)
                b = b000 + xf * (b111 - b011) + yf * (b011 - b001) + zf * (b001 - b000)
            } else if (zf > xf) {
                // g > b > r
                r = r000 + xf * (r111 - r011) + yf * (r010 - r000) + zf * (r011 - r010)
                g = g000 + xf * (g111 - g011) + yf * (g010 - g000) + zf * (g011 - g010)
                b = b000 + xf * (b111 - b011) + yf * (b010 - b000) + zf * (b011 - b010)
            } else {
                // g > r > b
                r = r000 + xf * (r110 - r010) + yf * (r010 - r000) + zf * (r111 - r110)
                g = g000 + xf * (g110 - g010) + yf * (g010 - g000) + zf * (g111 - g110)
                b = b000 + xf * (b110 - b010) + yf * (b010 - b000) + zf * (b111 - b110)
            }
        }

        outBuffer[ofs] = r
        outBuffer[ofs + 1] = g
        outBuffer[ofs + 2] = b
        outBuffer[ofs + 3] = buffer[ofs + 3] // Preserve alpha
    }

    opLog?.log("applyLut", image.width, image.height, `LUT ${LUT.size}³`, "Optimized")
    return output
}

/**
 * Optimized channel extraction using typed arrays.
 *
 * Uses direct typed array element access instead of byte-by-byte copy.
 * Enable with: CM_OPT_EXTRACT_CHANNEL=true
 */
export function extractChannelOptimized(input: ImageData, channel: number): ImageData {
    const opLog = OptimizedOps.logging() ? createOpLogger() : null
    const channels = input.channelLayout.length
    if (channel < 0 || channels - 1 < channel) throw Error(`Invalid channel idx ${channel}`)

    const output = createImageData(input.width, input.height, "L", input.dataType, input.colorSpace, input.dpi)
    const pixelCount = input.width * input.height

    // Use typed arrays directly for faster element-wise copy (vs byte-by-byte)
    if (input.dataType === "float32") {
        const inputArr = new Float32Array(input.data.buffer, input.data.byteOffset, input.data.byteLength / 4)
        const outputArr = new Float32Array(output.data.buffer, output.data.byteOffset, output.data.byteLength / 4)
        for (let i = 0; i < pixelCount; i++) {
            outputArr[i] = inputArr[i * channels + channel]
        }
    } else if (input.dataType === "uint16") {
        const inputArr = new Uint16Array(input.data.buffer, input.data.byteOffset, input.data.byteLength / 2)
        const outputArr = new Uint16Array(output.data.buffer, output.data.byteOffset, output.data.byteLength / 2)
        for (let i = 0; i < pixelCount; i++) {
            outputArr[i] = inputArr[i * channels + channel]
        }
    } else {
        // uint8
        const inputArr = new Uint8Array(input.data.buffer, input.data.byteOffset, input.data.byteLength)
        const outputArr = new Uint8Array(output.data.buffer, output.data.byteOffset, output.data.byteLength)
        for (let i = 0; i < pixelCount; i++) {
            outputArr[i] = inputArr[i * channels + channel]
        }
    }

    opLog?.log("extractChannel", input.width, input.height, `channel=${channel}`, "Optimized")
    return output
}

/**
 * Optimized channel combining using typed arrays.
 *
 * Uses direct typed array element interleaving instead of byte-by-byte copy.
 * Enable with: CM_OPT_COMBINE_CHANNELS=true
 */
export function combineChannelsOptimized(input: ImageData[], channelLayout: MultichannelLayouts): ImageData {
    const opLog = OptimizedOps.logging() ? createOpLogger() : null
    if (channelLayout.length !== input.length) throw Error(`Received ${input.length} inputs for ${channelLayout} layout. Expected ${channelLayout.length}`)
    const first = input[0]
    const channels = channelLayout.length

    // Validate all inputs match
    for (let i = 0; i < input.length; i++) {
        if (input[i].channelLayout !== "L") throw Error("Expected L channel layout for input")
        if (input[i].width !== first.width) throw Error("Expected same width for all inputs")
        if (input[i].height !== first.height) throw Error("Expected same height for all inputs")
        if (input[i].dataType !== first.dataType) throw Error("Expected same dataType for all inputs")
    }

    const output = createImageData(first.width, first.height, channelLayout, first.dataType, first.colorSpace, first.dpi)
    const pixelCount = first.width * first.height

    // Use typed arrays directly for faster element-wise interleaving (vs byte-by-byte)
    if (first.dataType === "float32") {
        const inputArrs = input.map((x) => new Float32Array(x.data.buffer, x.data.byteOffset, x.data.byteLength / 4))
        const outputArr = new Float32Array(output.data.buffer, output.data.byteOffset, output.data.byteLength / 4)
        for (let i = 0; i < pixelCount; i++) {
            const outBase = i * channels
            for (let j = 0; j < channels; j++) {
                outputArr[outBase + j] = inputArrs[j][i]
            }
        }
    } else if (first.dataType === "uint16") {
        const inputArrs = input.map((x) => new Uint16Array(x.data.buffer, x.data.byteOffset, x.data.byteLength / 2))
        const outputArr = new Uint16Array(output.data.buffer, output.data.byteOffset, output.data.byteLength / 2)
        for (let i = 0; i < pixelCount; i++) {
            const outBase = i * channels
            for (let j = 0; j < channels; j++) {
                outputArr[outBase + j] = inputArrs[j][i]
            }
        }
    } else {
        // uint8
        const inputArrs = input.map((x) => new Uint8Array(x.data.buffer, x.data.byteOffset, x.data.byteLength))
        const outputArr = new Uint8Array(output.data.buffer, output.data.byteOffset, output.data.byteLength)
        for (let i = 0; i < pixelCount; i++) {
            const outBase = i * channels
            for (let j = 0; j < channels; j++) {
                outputArr[outBase + j] = inputArrs[j][i]
            }
        }
    }

    opLog?.log("combineChannels", first.width, first.height, `-> ${channelLayout}`, "Optimized")
    return output
}

/**
 * TypeScript-based image creation implementation.
 * Used as fallback when Sharp is not available (e.g., in browser environments).
 */
export function createImageTS(
    width: number,
    height: number,
    dataType: TypedImageData["dataType"],
    colorSpace: TypedImageData["colorSpace"],
    color: number | readonly [number, number, number] | readonly [number, number, number, number],
    dpi?: number,
): ImageData {
    const opLog = OptimizedOps.logging() ? createOpLogger() : null
    const isGrayscale = typeof color === "number"
    const channelLayout: TypedImageData["channelLayout"] = isGrayscale ? "L" : color.length === 3 ? "RGB" : "RGBA"
    const numChannels = channelLayout.length

    const output = createImageData(width, height, channelLayout, dataType, colorSpace, dpi)
    const outputBuffer = getImageBufferFloat32(output)

    const colorValues = isGrayscale ? [color] : color

    let outputIndex = 0
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            for (let c = 0; c < numChannels; c++) {
                outputBuffer[outputIndex++] = colorValues[c]
            }
        }
    }

    opLog?.log("createImage", width, height, `${channelLayout} ${colorSpace}`, "TypeScript")
    return output
}
